import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../config/environments';
import { getGlobalLogoutHandler } from '../context/AuthContext';

// Create axios instance with base URL from environment config
const api = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Enable more detailed logging based on environment config
const DEBUG = config.debug;

// Flag to prevent multiple token refresh requests
let isRefreshing = false;
// Queue of requests that are waiting for token refresh
let refreshSubscribers: Array<(token: string) => void> = [];

// Function to add callbacks to the queue
const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Function to notify all subscribers with new token
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Add request interceptor to include auth token in requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Don't add auth headers for login/register endpoints
    const isAuthEndpoint = config.url?.includes('/login') || config.url?.includes('/register');
    
    if (!isAuthEndpoint) {
      const token = localStorage.getItem('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Log request details in debug mode
    if (DEBUG) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data,
        params: config.params
      });
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle common errors and token refresh
api.interceptors.response.use(
  (response) => {
    // Log response in debug mode
    if (DEBUG) {
      console.log(`API Response: ${response.status} ${response.config.url}`, {
        data: response.data,
        headers: response.headers
      });
    }
    return response;
  },
  async (error: AxiosError) => {
    // Log error in debug mode
    if (DEBUG) {
      console.error(`API Error: ${error.response?.status || 'Network Error'} ${error.config?.url}`, {
        data: error.response?.data,
        headers: error.response?.headers,
        message: error.message
      });
    }
    
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Skip token refresh for login/register endpoints
    const isAuthEndpoint = originalRequest.url?.includes('/login') || originalRequest.url?.includes('/register');
    
    // Handle 401 Unauthorized errors - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, wait for new token
        try {
          const newToken = await new Promise<string>((resolve) => {
            subscribeTokenRefresh((token: string) => {
              resolve(token);
            });
          });
          
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return api(originalRequest);
        } catch (err) {
          // If waiting for token refresh fails, use global logout handler
          const globalLogout = getGlobalLogoutHandler();
          if (globalLogout) {
            globalLogout();
          } else {
            // Fallback to redirect if AuthContext is not available
            window.location.href = '/login';
          }
          return Promise.reject(err);
        }
      }
      
      // Mark as retrying to prevent infinite loop
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        // Import here to avoid circular dependency
        // Using dynamic import to prevent circular dependency
        const { default: authService } = await import('./authService');
        const response = await authService.refreshToken();
        const newToken = response.accessToken;
        
        // Update token in original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        
        // Notify all waiting requests
        onTokenRefreshed(newToken);
        isRefreshing = false;
        
        // Retry original request with new token
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, use global logout handler
        const globalLogout = getGlobalLogoutHandler();
        if (globalLogout) {
          globalLogout();
        } else {
          // Fallback: manually clear localStorage if AuthContext is not available
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expiry');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
